import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { axiosApi } from "../helpers";
import { ENDPOINTS_RES, URL_RES } from "../constants/api_endpoints";
import { IResponseDashboardsList, IResponseIndexesList, IResState } from "../types/resTypes";
import { RequestStatus, ResponseStatus } from "../types/statusTypes";

const initialState: IResState = {
	dashboardsList: {
		data: {},
		activeDashboardLink: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	indexesList: {
		data: {},
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// список рабочих столов
export const getDashboardsList = createAsyncThunk("res/dashboardsList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseDashboardsList> = await axiosApi.get(`${URL_RES}/${ENDPOINTS_RES.DASHBOARDS}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// список индексов и полей
export const getIndexesList = createAsyncThunk("res/indexesList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseIndexesList> = await axiosApi.get(`${URL_RES}/${ENDPOINTS_RES.CUSTOM_FIELDS}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const resSlice = createSlice({
	name: "res",
	initialState,
	reducers: {
		// добавление ссылки на отчет
		addActiveDashboardLink: (state, action: PayloadAction<string>) => {
			state.dashboardsList.activeDashboardLink = action.payload;
		},
		// очистка state
		clearState: (state) => {
			state.dashboardsList = initialState.dashboardsList;
			state.indexesList = initialState.indexesList;
		},
		// очистка списка рабочих столов
		clearDashboardsList: (state) => {
			state.dashboardsList = initialState.dashboardsList;
		},
		// очистка списка индексов и полей
		clearIndexesList: (state) => {
			state.indexesList = initialState.indexesList;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getDashboardsList.pending, (state) => {
				state.dashboardsList.status = RequestStatus.LOADING;
			})
			.addCase(getDashboardsList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "dashboards" in action.payload && action.payload.dashboards) {
					state.dashboardsList.status = RequestStatus.IDLE;
					state.dashboardsList.data = action.payload.dashboards;
				} else state.dashboardsList.status = RequestStatus.FAILED;
			})
			.addCase(getDashboardsList.rejected, (state, action: PayloadAction<unknown>) => {
				state.dashboardsList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.dashboardsList.error = action.payload.response?.data.error;
					state.dashboardsList.message = action.payload.response?.data.message;
				}
			})
			.addCase(getIndexesList.pending, (state) => {
				state.indexesList.status = RequestStatus.LOADING;
			})
			.addCase(getIndexesList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "customfields" in action.payload && action.payload.customfields) {
					state.indexesList.status = RequestStatus.IDLE;
					state.indexesList.data = action.payload.customfields;
				} else state.indexesList.status = RequestStatus.FAILED;
			})
			.addCase(getIndexesList.rejected, (state, action: PayloadAction<unknown>) => {
				state.indexesList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.indexesList.error = action.payload.response?.data.error;
					state.indexesList.message = action.payload.response?.data.message;
				}
			});
	},
});

export const { addActiveDashboardLink, clearState, clearDashboardsList, clearIndexesList } = resSlice.actions;

export const selectDashboardsList = (state: RootState) => state.res.dashboardsList;
export const selectIndexesList = (state: RootState) => state.res.indexesList;

export default resSlice.reducer;
